import React from 'react';
import { InfoTooltipProps } from '../../../types/DesignSystem/Tooltip';
import Tooltip from './Tooltip';
import InfoGlyph from './InfoGlyph';
import { FOCUS_RING } from '../focusRing';
import { withClassName } from '../cn';

/**
 * The "ⓘ" affordance that explains a field.
 *
 * Replaces `OverlayIcon` **inside the design system**. That component lives at
 * `Components/GMS/Rules/Components/OverlayIcon` — a feature module — and was
 * imported by `Dropdown`, `TextField` and `FieldTitle`, which is how the design
 * system came to depend on a feature it is supposed to be built under. The ~40
 * consumers outside the design system keep using it untouched; migrating them is a
 * separate decision. See `okf/design-system/e2e.md` § 23.
 *
 * Three things changed in the port:
 *
 * - **It is a `<button>`.** `OverlayIcon` wrapped a bare `<svg>` in a `<div>` with
 *   mouse listeners, so the tooltip was unreachable by keyboard and the icon was
 *   not in the tab order at all.
 * - **The `clipPath` id comes from `useId`.** It was the literal string
 *   `clip0_3977_3515`, emitted once per instance — so a form with five info icons
 *   put five elements with one id in the document, and every `url(#…)` resolved to
 *   the first. Harmless while all five clip paths are identical, invalid either way,
 *   and the kind of thing that breaks the moment one of them differs.
 * - **The hit area is 24×24** while the glyph stays 16px, via a `::before` overlay
 *   that adds 4px on each side. WCAG 2.2's 2.5.8 (Target Size, Minimum) asks for
 *   24×24; padding the button instead would have grown the line box everywhere the
 *   icon sits inline next to a label.
 */
const InfoTooltip = React.forwardRef<HTMLButtonElement, InfoTooltipProps>(function InfoTooltip({
  message,
  placement = 'top',
  usePortal = true,
  label = 'More information',
  className = '',
  'data-testid': dataTestId,
  ...rest
}, ref) {
  // Nothing to explain, nothing to render — and no empty button in the tab order.
  if (message === undefined || message === null || message === '') return null;

  return (
    /*
      `tw-leading-[0]` + `tw-align-middle` on Tooltip's wrapper (§ 198). The wrapper
      is an inline-block span, so the page's inherited line-height (~21px at 14px
      text) set its height while the 16px button sat ON THE BASELINE inside it —
      measured live: the glyph's centre rode 2.5px above the label's centre in every
      `flex items-center` row (the Count/Quantity screenshot), because flex centres
      the inflated BOX, not the glyph. line-height: 0 kills the strut so the box IS
      the button (measured delta 0); align-middle covers plain inline placements,
      where flex isn't doing the centring. The inner measuring span inherits the
      line-height, so both boxes collapse.
    */
    <Tooltip content={message} placement={placement} usePortal={usePortal} className={withClassName(`tw-leading-[0] tw-align-middle`, className)}>
      <button
        {...rest}
        ref={ref}
        type="button"
        aria-label={label}
        data-testid={dataTestId}
        className={
          'tw-relative tw-inline-flex tw-w-4 tw-h-4 tw-items-center tw-justify-center '
          + 'tw-text-ink-tertiary hover:tw-text-ink-secondary tw-transition-colors motion-safe:tw-duration-fast motion-safe:tw-ease-swift tw-rounded-full '
          // 16px glyph, 24px target: 4px of invisible reach on every side.
          + "before:tw-content-[''] before:tw-absolute before:tw--inset-1 "
          + FOCUS_RING
        }
      >
        {/* `ink-tertiary` since § 189 — this is a LIVE control, and its glyph was
            `grey-500` (#97a0af, 2.64:1 on white), under the 3:1 that SC 1.4.11 asks
            of a meaningful graphic. The token clears it in both themes and follows
            `data-theme`, which the literal never could. (`OverlayIcon`, which this
            replaced, hardcoded `fill="#A7A9AF"` and could not respond to anything.) */}
        <InfoGlyph />
      </button>
    </Tooltip>
  );
});

export default InfoTooltip;
